/**
 * MethodModal Component
 *
 * Modal for method detail with full accessibility (WCAG 2.1 AA):
 * - aria-modal="true"
 * - aria-labelledby pointing to title
 * - Focus trap (Tab key stays within modal)
 * - Escape key closes modal
 * - Backdrop click closes modal
 * - Focus restoration on close
 * - Screen reader announcements
 * - Full i18n support with language context
 */

import React, { useEffect, useRef } from 'react';
import { parseMaterialsList } from '../types/method-fields';
import { useMethodDetail } from '../hooks/useMethods';
import { usePlan } from '../contexts/PlanContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ExpandableSection } from './ExpandableSection';
import { ResourceLink } from './ResourceLink';
import { MarkdownContent } from './MarkdownContent';

/**
 * Sanitize topic value by removing any HTML tags
 * This is a defensive measure for edge cases where HTML might slip through
 */
function sanitizeTopicValue(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .trim();
}

interface MethodModalProps {
  methodId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export const MethodModal: React.FC<MethodModalProps> = ({
  methodId,
  isOpen,
  onClose,
}) => {
  const { language, t, fieldLabels } = useLanguage();
  const { method, loading, error } = useMethodDetail(methodId, language);
  const { addMethod, showToast } = usePlan();
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Save trigger element on open
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;

      // Prevent background scroll
      document.body.style.overflow = 'hidden';

      // Focus modal on open
      setTimeout(() => {
        modalRef.current?.focus();
      }, 100);
    } else {
      // Restore background scroll
      document.body.style.overflow = '';

      // Restore focus to trigger element
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;

    const handleTab = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;

      const focusableElements = modal.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    modal.addEventListener('keydown', handleTab as EventListener);

    return () => {
      modal.removeEventListener('keydown', handleTab as EventListener);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent): void => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAddToPlan = (): void => {
    if (method) {
      addMethod(method);
    }
  };

  const handleDownloadMaterials = (): void => {
    if (!method?.facilitator_material_url || typeof method.facilitator_material_url !== 'string') return;
    window.open(method.facilitator_material_url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadPDF = async (): Promise<void> => {
    if (!method) return;

    const apiUrl = typeof window !== 'undefined' && window.tpSettings?.apiUrl
      ? window.tpSettings.apiUrl
      : '/wp-json/training-planner/v1';

    try {
      // PHP streams PDF bytes directly — no temp file, no second fetch
      const response = await fetch(`${apiUrl}/methods/${method.id}/pdf`, {
        headers: {
          'X-WP-Nonce': window.tpNonce?.rest ?? '',
        },
      });

      if (!response.ok) {
        showToast('PDF generation failed. Please try again.', 'error');
        return;
      }

      const contentType = response.headers.get('Content-Type') ?? '';
      if (!contentType.includes('application/pdf')) {
        showToast('PDF generation failed. Please try again.', 'error');
        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${method.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);

      // Track PDF export event
      window.goatcounter?.count({ path: 'pdf-export-method', title: 'Method PDF Export', event: true });
    } catch (err) {
      console.error('PDF download error:', err);
      showToast('PDF download failed. Please try again.', 'error');
    }
  };

  return (
    <div
      role="presentation"
      className="modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby={method ? 'modal-description' : undefined}
        tabIndex={-1}
        className="modal-content"
      >
        {/* Header */}
        <div className="modal-header flex items-start justify-between">
          <h2 id="modal-title" className="modal-title flex-1">
            {method?.title || t('loading')}
          </h2>
          <button
            onClick={onClose}
            aria-label={t('closeModal')}
            className="modal-close shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Loading state */}
          {loading && (
            <div role="status" aria-live="polite" className="flex items-center justify-center py-12">
              <div className="spinner"></div>
              <span className="ml-3 text-gray-600">{t('loadingMethodDetails')}</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div role="alert" aria-live="assertive" className="alert alert-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Method content */}
          {method && (
            <>
              {/* Header with Contributor */}
              {method.contributor?.value && (
                <div className="mb-4">
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    <span className="font-medium">{fieldLabels.contributor}:</span>{' '}
                    <span className="text-zinc-800 dark:text-zinc-200">{method.contributor.value}</span>
                  </p>
                </div>
              )}

              {/* Metadata */}
              <div className="bg-zinc-50 dark:bg-zinc-800 rounded-lg p-4 mb-6 space-y-3">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    <strong>{fieldLabels.duration}:</strong> {method.duration_min}-{method.duration_max} min
                  </span>
                </div>

                {method.modules && method.modules.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{fieldLabels.module}:</span>
                    {method.modules.map((m) => (
                      <span key={m.id} className="badge badge-teal">
                        {m.name}
                      </span>
                    ))}
                  </div>
                )}

                {method.topics && method.topics.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{fieldLabels.topics}:</span>
                    {method.topics.map((t, index) => (
                      <span key={index} className="badge badge-secondary">
                        {sanitizeTopicValue(t.value)}
                      </span>
                    ))}
                  </div>
                )}

                {method.tags && method.tags.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{fieldLabels.activity_types}:</span>
                    {method.tags.map((t) => (
                      <span key={t.id} className="badge badge-primary">
                        {t.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              {method.content && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-3 flex items-center gap-2">
                    <span aria-hidden="true">📖</span>
                    {fieldLabels.description}
                  </h3>
                  <MarkdownContent
                    content={method.content}
                    testId="method-description"
                  />
                </div>
              )}

              {/* Learning Goals - Always visible when present */}
              {method.learning_goals?.content && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-3 flex items-center gap-2">
                    <span aria-hidden="true">🎯</span>
                    {fieldLabels.learning_goals}
                  </h3>
                  <MarkdownContent
                    content={method.learning_goals.content}
                    testId="method-learning-goals"
                  />
                </div>
              )}

              {/* Preparation Notes - Expandable */}
              {method.preparation_notes?.content && (
                <ExpandableSection
                  title={fieldLabels.preparation}
                  content={method.preparation_notes.content}
                  icon={<span aria-hidden="true">✅</span>}
                  isMarkdown={true}
                  testId="preparation-section"
                />
              )}

              {/* Materials - Expandable, only if flag=true */}
              {method.materials_needed?.flag && method.materials_needed?.content && (
                <ExpandableSection
                  title={fieldLabels.materials}
                  content=""
                  icon={<span aria-hidden="true">📦</span>}
                  testId="materials-section"
                >
                  <ul className="materials-list list-disc list-inside space-y-1">
                    {parseMaterialsList(method.materials_needed.content).map((item, index) => (
                      <li key={index} className="materials-list-item text-zinc-700 dark:text-zinc-300">
                        {item}
                      </li>
                    ))}
                  </ul>
                </ExpandableSection>
              )}

              {/* Online Resources - Separate section */}
              {method.online_materials?.flag && method.online_materials?.url && (
                <ResourceLink
                  title={fieldLabels.resources}
                  url={method.online_materials.url}
                  testId="resources-section"
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {method && (
          <div className="modal-footer">
            <button
              data-testid="add-to-plan-btn"
              onClick={handleAddToPlan}
              aria-label={t('addToPlan')}
              className="btn-secondary flex-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('addToPlan')}
            </button>

            {method.facilitator_material_url && (
              <button
                data-testid="download-materials-btn"
                onClick={handleDownloadMaterials}
                aria-label={t('downloadMaterials')}
                className="btn-outline flex-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                {t('downloadMaterials')}
              </button>
            )}

            <button
              data-testid="download-pdf-btn"
              onClick={handleDownloadPDF}
              aria-label={t('downloadPdf')}
              className="btn-danger flex-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('downloadPdf')}
            </button>
          </div>
        )}

        {/* Screen reader announcement region */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {isOpen && 'Method details dialog opened'}
        </div>
      </div>
    </div>
  );
};
